import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { event as eventApi } from '../../utils/api';
import { toast } from 'react-toastify';
import { getTransformedImageUrl } from '../../utils/filestack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReactCountryFlag from 'react-country-flag';
import { COUNTRY_TO_CODE } from '../../utils/countryUtils';
import PublicNavbar from '../../components/shared/PublicNavbar';

const LandingEventsPage = () => {
  const { t } = useTranslation('events');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventIndex, setSelectedEventIndex] = useState(0);
  const datesContainerRef = useRef(null);
  
  useEffect(() => {
    fetchEvents();
  }, []);
  
  useEffect(() => {
    // Scroll to center the selected event in the dates list
    if (datesContainerRef.current) {
      const container = datesContainerRef.current;
      const selectedElement = container.querySelector(`[data-index="${selectedEventIndex}"]`);
      
      if (selectedElement) {
        const containerWidth = container.offsetWidth;
        const elementLeft = selectedElement.offsetLeft;
        const elementWidth = selectedElement.offsetWidth;
        
        // Calculate scroll position to center the element
        const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
        
        // Use smooth scrolling for a better user experience
        container.scrollTo({ 
          left: scrollPosition, 
          behavior: selectedEventIndex === 0 ? 'auto' : 'smooth' 
        });
      }
    }
  }, [selectedEventIndex]);
  
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await eventApi.getPublic({ 
        showPast: false // Changed to false to only get upcoming events
      });
      
      console.log('Raw events data from API:', {
        count: data.length,
        firstEvent: data[0],
        hasImageUrls: data.filter(e => e.imageUrl).length,
        hasRacetracks: data.filter(e => e.racetrack).length,
        isRacedays: data.filter(e => e.isRaceday).length,
        eventTypes: data.filter(e => e.eventType === 'raceday').length
      });
      
      // Get current date for comparison
      const now = new Date();
      
      // Filter events that have necessary data to display and are upcoming
      const raceEvents = data.filter(event => {
        console.log('Checking event:', {
          id: event._id,
          title: event.title,
          isRaceday: event.isRaceday,
          hasImage: !!event.imageUrl,
          hasRacetrack: !!event.racetrack,
          eventType: event.eventType,
          date: event.date,
          isUpcoming: new Date(event.date) >= now
        });
        
        // We need events with images and racetracks that are upcoming
        return event.imageUrl && event.racetrack && new Date(event.date) >= now;
      });
      
      console.log('Filtered upcoming race events:', {
        count: raceEvents.length,
        firstEvent: raceEvents[0],
      });
      
      // Sort upcoming events by date (closest first)
      raceEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      console.log('Filtered events for landing page:', {
        totalEvents: data.length,
        upcomingEvents: raceEvents.length
      });
      
      setEvents(raceEvents);
      
      // Set the default selected event to the first upcoming event
      if (raceEvents.length > 0) {
        setSelectedEventIndex(0);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      // Don't show error toast on public page to avoid confusion
      // Just set empty events array instead
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEventClick = (index) => {
    setSelectedEventIndex(index);
  };
  
  const handleViewDetails = () => {
    if (events[selectedEventIndex]) {
      navigate(`/events/${events[selectedEventIndex]._id}`);
    }
  };
  
  const handleNextEvent = () => {
    if (selectedEventIndex < events.length - 1) {
      setSelectedEventIndex(selectedEventIndex + 1);
    }
  };
  
  const handlePrevEvent = () => {
    if (selectedEventIndex > 0) {
      setSelectedEventIndex(selectedEventIndex - 1);
    }
  };
  
  const handleScrollLeft = () => {
    if (datesContainerRef.current) {
      datesContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };
  
  const handleScrollRight = () => {
    if (datesContainerRef.current) {
      datesContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };
  
  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: format(date, 'dd'),
      month: format(date, 'MMM'),
      year: format(date, 'yyyy')
    };
  };
  
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }
  
  if (events.length === 0) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor="background.default"
        p={3}
      >
        <Typography variant="h4" gutterBottom>
          {t('landingEvents.noEvents')}
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/events')}
          sx={{ mt: 2 }}
        >
          {t('landingEvents.viewAllEvents')}
        </Button>
      </Box>
    );
  }
  
  const selectedEvent = events[selectedEventIndex];
  const eventDate = formatEventDate(selectedEvent.date);
  const countryCode = selectedEvent.racetrack && selectedEvent.racetrack.country ? 
    COUNTRY_TO_CODE[selectedEvent.racetrack.country] : null;
  
  return (
    <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
      {/* Background Image with Overlay */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedEvent._id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            backgroundImage: `url(${getTransformedImageUrl(selectedEvent.imageUrl, isMobile ? 'gallery' : 'full')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to right, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.7)} 40%, ${alpha(theme.palette.background.default, 0.5)} 60%, transparent 100%)`,
              zIndex: 1,
            }}
          />
        </motion.div>
      </AnimatePresence>

      <PublicNavbar />
      
      {/* Top dates navigation */}
      <Box 
        sx={{ 
          position: 'relative',
          width: '100%',
          pt: 20,
          pb: 2,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            {/* Dates container */}
            <Box 
              ref={datesContainerRef}
              sx={{ 
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                py: 2,
                gap: { xs: 2, md: 4 },
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                msOverflowStyle: 'none',
                px: 2
              }}
            >
              {events.map((event, index) => {
                const date = formatEventDate(event.date);
                const isSelected = index === selectedEventIndex;
                const isPast = new Date(event.date) < new Date();
                const countryCode = event.racetrack && event.racetrack.country ? 
                  COUNTRY_TO_CODE[event.racetrack.country] : null;
                
                return (
                  <Box
                    key={event._id}
                    data-index={index}
                    component={motion.div}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => handleEventClick(index)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      position: 'relative',
                      opacity: isPast ? 0.6 : 1,
                      minWidth: { xs: '80px', md: '100px' },
                      pb: 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Typography 
                      variant="h3" 
                      component="div"
                      sx={{ 
                        fontWeight: 800,
                        fontSize: { xs: '2rem', md: '3rem' },
                        lineHeight: 0.9,
                        color: isSelected ? 'primary.main' : 'text.primary',
                        mb: 1
                      }}
                    >
                      {date.day}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 0.5
                    }}>
                      {countryCode && (
                        <ReactCountryFlag 
                          countryCode={countryCode} 
                          svg 
                          style={{ 
                            width: '1.2em', 
                            height: '1.2em',
                            marginRight: '6px',
                            borderRadius: '2px'
                          }}
                        />
                      )}
                      <Typography 
                        variant="subtitle1"
                        sx={{ 
                          textTransform: 'uppercase',
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', md: '0.9rem' },
                          color: isSelected ? 'primary.main' : 'text.secondary'
                        }}
                      >
                        {date.month}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            
          </Box>
        </Container>
      </Box>
      
      {/* Content */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          height: 'calc(100vh - 150px)', 
          position: 'relative', 
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          pt: 4
        }}
      >
        {/* Event details */}
        <Box 
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: { xs: 4, md: 0 }
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedEvent._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: 0.5,
                type: "spring",
                stiffness: 100,
                damping: 15
              }}
            >
              <Box 
              padding={4}
              >
                {/* Event title */}
                <Typography 
                  variant="h1" 
                  component="h1"
                  sx={{ 
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5rem' },
                    lineHeight: 1.1,
                    mb: 2,
                  }}
                >
                  {selectedEvent.title}
                </Typography>
                
                {/* Event details */}
                <Box sx={{ mb: 4 }}>
                  {/* Location */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOnIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="span">
                      {selectedEvent.racetrack ? selectedEvent.racetrack.name : selectedEvent.location}
                    </Typography>
                  </Box>
                  
                  {/* Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="span">
                      {format(new Date(selectedEvent.date), 'PPP')}
                      {selectedEvent.endDate && ` - ${format(new Date(selectedEvent.endDate), 'PPP')}`}
                    </Typography>
                  </Box>
                </Box>
                
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingEventsPage; 